(ns frontend.view.nev.surveys.enrich
  "AI enrichment for survey processes: drop a PDF, the AI answers the chosen
   template's questions, and the answers pre-fill the editor for review.

   Survey questions are not entity-schema attributes — they live in the process's
   burned template config (`:survey-process/burned-survey-config`), blocks ->
   content -> question items. So the field set is advertised to the model as
   inline `:field-specs` built from that config, and answers are applied through
   the survey editor's own on-answer-input events (per-question id -> value),
   not the generic entity update the curtain/TP flows use."
  (:require
   [clojure.string :as str]
   [common.localization :refer [tr]]
   [frontend.state.current-session :as session]
   [frontend.view.nev.surveys.form.sub :as survey-form-sub]
   [re-frame.core :as rf]
   [reagent.core :as r]
   [ui.components.alert.view :as alert]
   [ui.enrich.apply :as enrich-apply]
   [ui.enrich.drop-section :as enrich-drop]
   [ui.enrich.event :as enrich-event]
   [ui.enrich.model :as enrich-model]
   [ui.enrich.sub :as enrich-sub]
   [ui.view.survey.event :as survey-event]))

;; The model answers under string keys; a question's id becomes "q<id>".
(defn question-key [question-id] (keyword (str "q" question-id)))
(defn key->question-id [k] (js/parseInt (subs (name k) 1)))

(def ^:private skip-data-types
  "Question types we don't advertise: file uploads and read-only calculated values."
  #{:data.type/file :data.type/calculated-number})

(defn- lcase [s] (some-> s str str/lower-case str/trim))

(defn config-questions
  "Flat seq of question items from a burned survey config (walks all blocks'
   content, keeping the question items)."
  [config]
  (->> (:blocks config)
       (mapcat :content)
       (filter #(= :survey-item/question (:survey-item-type %)))))

(defn- q-label [lang q]
  (or (get-in q [:survey-question/label-translations lang])
      (some-> q :survey-question/label-translations first val)))

(defn question-options
  "A question's choices as [{:value <option-id> :label <text>}], or nil. Option
   labels in the burned config may themselves be per-language maps."
  [lang q]
  (seq (->> (:survey-question/options q)
            (map (fn [o]
                   (let [l (:label o)]
                     {:value (:value o)
                      :label (if (map? l) (or (get l lang) (some-> l first val)) l)}))))))

(defn question-field-specs
  "Inline enrichment field specs for a burned survey `config`, skipping file and
   calculated questions."
  [lang config]
  (->> (config-questions config)
       (remove #(contains? skip-data-types (:survey-question/data-type %)))
       (keep (fn [q]
               (when-let [label (q-label lang q)]
                 (cond-> {:key (question-key (:survey-question-id q)) :label label}
                   (:survey-question/data-type q) (assoc :data-type (:survey-question/data-type q))
                   (question-options lang q) (assoc :options (question-options lang q))))))
       vec))

(defn- resolve-option
  "Map a loose model value to one of `options`' ids, by label or value."
  [options raw]
  (let [t (lcase raw)]
    (some (fn [{:keys [value label]}]
            (when (or (= t (lcase label)) (= t (lcase value)) (= t (lcase (str value))))
              value))
          options)))

(defn- clean-answer
  "Clean a model answer to the stored value for `data-type`. Choice questions
   resolve to option ids; everything else reuses the shared scalar cleaning."
  [data-type options raw]
  (cond
    (= data-type :data.type/ref-single)
    (resolve-option options raw)

    (= data-type :data.type/ref-multi)
    (let [items (if (coll? raw) raw [raw])
          vs    (vec (keep #(resolve-option options %) items))]
      (when (seq vs) vs))

    :else
    (let [v (enrich-apply/clean-value data-type options raw)]
      (when-not (= enrich-apply/drop-marker v) v))))

(defn- questions-by-id [config]
  (into {} (map (juxt :survey-question-id identity) (config-questions config))))

(defn answer-updates
  "{question-id -> cleaned answer value} from a completed survey enrichment result
   against the burned `config`, dropping unknown/uncleanable answers."
  [result lang config]
  (let [fields (some-> result :suggestions :targets :survey-process first :fields)
        qmap   (questions-by-id config)]
    (into {}
          (keep (fn [[k {:keys [value]}]]
                  (let [qid (key->question-id k)
                        q   (get qmap qid)]
                    (when q
                      (let [cleaned (clean-answer (:survey-question/data-type q)
                                                  (question-options lang q)
                                                  value)]
                        (when (some? cleaned) [qid cleaned])))))
                fields))))

;; ---------------------------------------------------------------------------
;; UI: drop-zone + completion applier for the survey editor
;; ---------------------------------------------------------------------------

(defn survey-result?
  "True for an enrichment result that belongs to a survey process."
  [result]
  (and result
       (= :entity.type/survey-process (-> result :request-context :entity-type))))

(defn job-to-apply
  "The finished job whose answers the editor of `process-id` applies: the one
   its drop section `started`, else, until the editor has applied one, the
   newest `leftover` from a reload. Once it has applied a job it never falls
   back to another: when those answers are saved their job is resolved, and
   an older job would overwrite them."
  [process-id started leftover applied]
  (or (when (= process-id (enrich-model/job-survey-process-id started))
        started)
      (when (nil? applied)
        leftover)))

(defn- request-fn
  [process-id lang config attachment-id]
  {:entity-type    :entity.type/survey-process
   :targets        [{:entity-type :entity.type/survey-process
                     :slug        "survey-process"
                     :field-specs (question-field-specs lang config)}]
   :attachment-ids [attachment-id]
   :draft          {:survey-process-id process-id}})

(defn drop-and-apply
  "Rendered in the survey answer editor: a PDF drop-zone advertising the burned
   config's questions, plus an applier that fills the editor with the answers
   of this process's finished job (see job-to-apply). The job stays listed
   until the answers are saved or the editor is left without saving.
   `:answer-series` defaults to 0 (single-series / first period).

   A completed result with nothing to apply says so, rather than leaving the
   user wondering whether anything ran. The notice goes once they close it or
   start another enrichment."
  [_props]
  (let [applied    (r/atom nil)
        no-values? (r/atom false)
        token      (r/atom nil)  ; the job the drop section started
        shown      (r/atom nil)  ; the process the state above is about
        fetching   (r/atom nil)] ; the job whose answers are being fetched
    (fn [{:keys [lang answer-series] :or {answer-series 0}}]
      (let [config       @(rf/subscribe [::survey-form-sub/new-config])
            process-id   (:db/id config)
            _            (when (not= process-id @shown)
                           ;; The editor moved to another process: its job is not
                           ;; this one's.
                           (reset! shown process-id)
                           (reset! token nil)
                           (reset! applied nil)
                           (reset! no-values? false))
            lang         (or lang (:default-lang config) :ENG)
            specs        (question-field-specs lang config)
            started      @(rf/subscribe [::enrich-sub/by-token @token])
            result       (job-to-apply process-id started
                                       @(rf/subscribe [::enrich-sub/survey-job process-id])
                                       @applied)
            in-progress? (enrich-model/in-progress-status? (:status started))]
        (when (and in-progress? @no-values?)
          (reset! no-values? false))
        (when (and (survey-result? result)
                   (= process-id (enrich-model/job-survey-process-id result))
                   (= :ai.status/completed (:status result))
                   (not= (:result-id result) @applied))
          (if (:loaded? result)
            (let [updates (answer-updates result lang config)]
              (reset! applied (:result-id result))
              (reset! no-values? (empty? updates))
              (doseq [[qid v] updates]
                (rf/dispatch [::survey-event/on-answer-input
                              process-id answer-series qid v true]))
              (rf/dispatch [::enrich-event/survey-applied process-id (:result-id result)]))
            (when (not= (:result-id result) @fetching)
              (reset! fetching (:result-id result))
              (rf/dispatch [::enrich-event/fetch-result (:result-id result) {}]))))
        (when (and (seq specs)
                   (session/has-feature-access? :feature/ai-enrichment))
          [:div {:class "flex flex-col gap-2"}
           [enrich-drop/section
            {:request-fn (partial request-fn process-id lang config)
             :token-atom token}]
           (when @no-values?
             [alert/info
              {:on-close (fn [] (reset! no-values? false))}
              (tr :transaction-process/enrich-no-values)])])))))
